import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useV3Api } from '../api/useV3Api';
import { errorText } from '../api/errorText';
import ColumnHeads from '../components/drift/ColumnHeads';
import CveBlock from '../components/drift/CveBlock';
import PageHeader from '../components/PageHeader';
import ShowCommand from '../components/ShowCommand';
import { evaluateText, gapText, severityClass } from '../drift/findingModel';
import { VIEWS, asOfText, blocksFor, capabilityOf, count, scopeOf, tilesOf, worstFirst } from '../drift/summaryModel';
import { usePlans } from '../drift/usePlans';
import { useTableView } from '../drift/useTableView';
import { GLOBAL, useV3Account } from '../useV3Account';

const ROUTE = 'v3/summary';

const TOP = [
  { key: 'subject', label: 'What', text: (r) => r.subjectName || r.subject },
  { key: 'gap', label: 'Kind', text: (r) => gapText(r.gap) },
  { key: 'severity', label: 'Worst', text: (r) => r.severity, options: true, order: ['critical', 'high', 'medium', 'low', 'info'] },
  { key: 'machines', label: 'Machines', text: (r) => String(r.machines), sort: (r) => r.machines },
];

const CUSTOMERS = [
  { key: 'account', label: 'Customer', text: (a) => `${a.account} ${a.name || ''}`.trim() },
  { key: 'critical', label: 'Critical', text: (a) => String(a.__scope.bySeverity.critical || 0), sort: (a) => a.__scope.bySeverity.critical || 0 },
  { key: 'high', label: 'High', text: (a) => String(a.__scope.bySeverity.high || 0), sort: (a) => a.__scope.bySeverity.high || 0 },
  { key: 'findings', label: 'Open findings', text: (a) => String(a.__scope.findings), sort: (a) => a.__scope.findings },
  { key: 'machines', label: 'Machines', text: (a) => String(a.__scope.machines), sort: (a) => a.__scope.machines },
  { key: 'needs', label: 'Needs a person', text: (a) => String(a.__scope.needsPerson || 0), sort: (a) => a.__scope.needsPerson || 0 },
  { key: 'plans', label: 'Plans open', text: (a) => String(a.__scope.plansOpen || 0), sort: (a) => a.__scope.plansOpen || 0 },
  { key: 'ending', label: 'Exceptions ending', text: (a) => String(count(a.__scope.exceptions, 'ending')), sort: (a) => count(a.__scope.exceptions, 'ending') },
  { key: 'asOf', label: 'As of', text: (a) => (a.lastEvaluated ? a.lastEvaluated.utc.slice(0, 16).replace('T', ' ') : 'never'), sort: (a) => (a.lastEvaluated ? a.lastEvaluated.utc : '') },
];

// One capability on the dashboard: how many open findings on how many machines and how bad, a link to them on the Findings page, and the
// subjects that affect the most machines. Each capability has its own list so one cannot crowd another out.
// Tick subjects and Create actions: one action plan for each open finding of each ticked subject, on each machine it affects (Docker Desktop on two
// machines is two plans), made by the finding's own recommended action. The findings are read when the button is pressed, so the plans are for what
// is open now. Nothing is queued: that is the Actions page. Not offered for All customers (a plan belongs to one customer).
function Block({ block, data, account, plans, get }) {
  const top = (data && data.top) || [];
  const view = useTableView(top, TOP);
  const selectable = !!account && account !== GLOBAL;
  const keyOf = (r) => `${r.capability}|${r.subject}`;
  const [picked, setPicked] = useState({});
  const [working, setWorking] = useState(false);
  const [readError, setReadError] = useState('');
  const chosen = view.rows.filter((r) => picked[keyOf(r)]);
  const planCount = chosen.reduce((n, r) => n + (r.findings || 1), 0);       // one plan for each finding: a subject on two machines is two
  const allShown = view.rows.length > 0 && chosen.length === view.rows.length;
  const createActions = async () => {
    setWorking(true);
    setReadError('');
    try {
      const subjects = new Set(chosen.map(keyOf));
      const capabilities = [...new Set(chosen.map((r) => r.capability))];
      const found = [];
      for (const capability of capabilities) {
        const body = await get('v3/findings', { account, capability, state: 'open' });
        ((body && body.items) || []).forEach((f) => { if (subjects.has(`${f.capability}|${f.subject}`) && f.gap !== 'unlisted') found.push(f); });
      }
      await plans.proposeMany(found);
      setPicked({});
    } catch (ex) {
      setReadError(errorText(ex));
    } finally {
      setWorking(false);
    }
  };
  const scope = account && account !== GLOBAL ? `account=${encodeURIComponent(account)}&` : '';
  return (
    <div className="card" role="region" aria-label={block.title}>
      <div className="card-head">
        <div><h3>{block.title}</h3><div className="sub">{block.hint}</div></div>
        <Link className="btn btn-sm" to={`/v3/findings?${scope}capability=${block.key}`}>Open the findings</Link>
      </div>
      <div className="card-body">
        <div className="sub block-line">
          {data.findings} open finding{data.findings === 1 ? '' : 's'} on {data.machines} machine{data.machines === 1 ? '' : 's'}
          {data.worst && <> , worst <span className={`pill ${severityClass(data.worst)}`}>{data.worst}</span></>}
          {data.needsPerson > 0 && `, ${data.needsPerson} need a person`}.
        </div>
      </div>
      {selectable && top.length > 0 && (
        <div className="bulk-bar block-bulk" role="group" aria-label={`${block.title}: selected subjects`}>
          <span className="sub">{chosen.length} selected of {view.rows.length} shown</span>
          <button type="button" className="btn btn-sm" disabled={chosen.length === 0 || working || !!plans.busy} onClick={createActions}>
            {working || plans.busy ? 'Working...' : `Create actions (${planCount})`}
          </button>
        </div>
      )}
      {readError && <div className="field-error" role="alert">{readError}</div>}
      {top.length === 0 ? <div className="empty">Nothing is open.</div> : (
        <div className="table-wrap">
          <table className="open-rows" aria-label={`${block.title} subjects`}>
            <thead><ColumnHeads view={view} select={selectable ? { checked: allShown, indeterminate: chosen.length > 0 && !allShown, label: `Select every ${block.title} subject shown`, onChange: (e) => setPicked(e.target.checked ? Object.fromEntries(view.rows.map((r) => [keyOf(r), true])) : {}) } : null} /></thead>
            <tbody>
              {view.rows.length === 0 && <tr><td colSpan={TOP.length + (selectable ? 1 : 0)} className="empty">Nothing matches the column filters.</td></tr>}
              {view.rows.map((r) => (
                <tr key={keyOf(r)}>
                  {selectable && <td><input type="checkbox" aria-label={`Select ${r.subjectName || r.subject}`} checked={!!picked[keyOf(r)]} onChange={(e) => setPicked((prev) => ({ ...prev, [keyOf(r)]: e.target.checked }))} /></td>}
                  <td className="strong">{r.subjectName || r.subject}</td><td>{gapText(r.gap)}</td>
                  <td><span className={`pill ${severityClass(r.severity)}`}>{r.severity}</span></td><td>{r.machines}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Where V3 opens: what the stored findings, plans and exceptions add up to, for one customer or for every customer, as of the last evaluation
// (which it says: nothing here reads VES or evaluates). The view chooses one of drift (software), patch or CVE; each tile is a link to the page
// it counts, already filtered. Unlisted software is counted apart, as it is most of the findings and mostly nothing to act on.
export default function DashboardPage() {
  const { get, post } = useV3Api();
  const navigate = useNavigate();
  const { accounts, account, choose, loading: accountsLoading, error: accountsError } = useV3Account({ withGlobal: true, globalLabel: 'All customers' });
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [view, setView] = useState('drift');
  const [reloads, setReloads] = useState(0);
  const [running, setRunning] = useState(false);
  const [evaluated, setEvaluated] = useState({ notice: '', problem: '' });
  const code = account && account.code;
  const all = code === GLOBAL;
  const plans = usePlans(all ? null : code, { load: false });       // only to create actions for the subjects ticked

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    get(ROUTE, { account: code })
      .then((body) => { if (!cancelled) setState({ loading: false, data: body, error: '' }); })
      .catch((ex) => { if (!cancelled) setState({ loading: false, data: null, error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, code, reloads]);

  // Judge the customer again for the capability of the view only (POST v3/evaluate, as on the Findings page), then read the numbers again.
  const evaluate = async () => {
    setRunning(true);
    setEvaluated({ notice: '', problem: '' });
    try {
      const result = await post('v3/evaluate', { account: code, capability: capabilityOf(view) });
      setEvaluated({ notice: evaluateText(result), problem: '' });
      setReloads((n) => n + 1);
    } catch (ex) {
      setEvaluated({ notice: '', problem: errorText(ex) });
    } finally {
      setRunning(false);
    }
  };
  const viewLabel = (VIEWS.find((v) => v.key === view) || VIEWS[0]).label;

  const summary = state.data && (all ? state.data.totals : state.data);
  const scope = scopeOf(summary, view);
  const customers = useMemo(() => (all && state.data
    ? worstFirst(state.data.accounts.map((a) => ({ ...a, __scope: scopeOf(a, view) })).map((a) => ({ ...a, bySeverity: a.__scope.bySeverity, findings: a.__scope.findings })))
    : []), [all, state.data, view]);
  const customerView = useTableView(customers, CUSTOMERS);
  const tiles = summary ? tilesOf(scope, code, capabilityOf(view)) : [];
  const blocks = blocksFor(view);

  return (
    <>
      <PageHeader
        title="Dashboard"
        bear="kongiDashboard"
        subtitle={summary ? asOfText(summary.lastEvaluated) : 'What is wrong across the customers, as of the last evaluation.'}
        actions={<ShowCommand route={ROUTE} params={{ account: code }} disabled={!code} />}
      />

      {accountsError && <div className="field-error" role="alert">{accountsError}</div>}
      {plans.message.notice && <div className="notice" role="status">{plans.message.notice} <Link to={`/v3/actions?account=${encodeURIComponent(code)}`}>Open the actions</Link></div>}
      {plans.message.problem && <div className="field-error" role="alert">{plans.message.problem}</div>}
      {state.error && <div className="field-error" role="alert">{state.error}</div>}
      {evaluated.notice && <div className="notice" role="status">{evaluated.notice}</div>}
      {evaluated.problem && <div className="field-error" role="alert">{evaluated.problem}</div>}

      <div className="card">
        <div className="filter-bar toolbar">
          <div className="field inline">
            <label htmlFor="v3-account">Customer</label>
            <select id="v3-account" value={code || ''} disabled={accountsLoading || accounts.length === 0} onChange={(e) => choose(e.target.value)}>
              {accounts.map((a) => <option key={a.code} value={a.code}>{a.code === GLOBAL ? a.name : `${a.code} - ${a.name}`}</option>)}
            </select>
          </div>
          <div className="chips view-chips" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <button type="button" key={v.key} className={`chip${view === v.key ? ' active' : ''}`} aria-pressed={view === v.key} onClick={() => setView(v.key)}>{v.label}</button>
            ))}
          </div>
          <div className="evaluate-bar">
            <button type="button" className="btn btn-primary btn-sm" disabled={!code || all || running} title={all ? 'Choose a customer to evaluate' : undefined} onClick={evaluate}>{running ? 'Working...' : `Evaluate ${viewLabel}`}</button>
          </div>
        </div>
        {state.loading && <div className="empty">Loading...</div>}
        {summary && (
          <div className="card-body">
            <div className="tiles" role="list" aria-label="Summary">
              {tiles.map((t) => (
                <Link key={t.key} to={t.to} role="listitem" className={`tile tone-${t.tone}`} title={t.hint}>
                  <span className="tile-value">{t.value}</span>
                  <span className="tile-label">{t.label}</span>
                </Link>
              ))}
            </div>
            <div className="sub dash-line">
              {scope.findings} open finding{scope.findings === 1 ? '' : 's'} on {scope.machines} machine{scope.machines === 1 ? '' : 's'}.
              {view === 'drift' && summary.unlisted > 0 && ` ${summary.unlisted} unlisted software finding${summary.unlisted === 1 ? ' is' : 's are'} not counted: they are on the Findings page under "Include unlisted software".`}
            </div>
          </div>
        )}
      </div>

      {summary && all && (
        <div className="card">
          <div className="card-head"><div><h3>Customers</h3><div className="sub">Worst first. Click a customer to open its findings.</div></div></div>
          {customers.length === 0 ? <div className="empty">No customers.</div> : (
            <div className="table-wrap">
              <table className="open-rows" aria-label="Customers">
                <thead><ColumnHeads view={customerView} /></thead>
                <tbody>
                  {customerView.rows.length === 0 && <tr><td colSpan={CUSTOMERS.length} className="empty">No customers match the column filters.</td></tr>}
                  {customerView.rows.map((a) => (
                    <tr key={a.account} className="row" data-testid={`customer-${a.account}`} tabIndex={0} onClick={() => navigate(`/v3/findings?account=${encodeURIComponent(a.account)}`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/v3/findings?account=${encodeURIComponent(a.account)}`); }}>
                      <td className="strong">{a.account}<div className="sub">{a.name}</div></td>
                      <td>{a.__scope.bySeverity.critical || 0}</td><td>{a.__scope.bySeverity.high || 0}</td><td>{a.__scope.findings}</td><td>{a.__scope.machines}</td>
                      <td>{a.__scope.needsPerson || 0}</td><td>{a.__scope.plansOpen || 0}</td><td>{count(a.__scope.exceptions, 'ending')}</td>
                      <td className="sub nowrap">{a.lastEvaluated ? a.lastEvaluated.utc.slice(0, 16).replace('T', ' ') : 'never'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {summary && blocks.map((b) => (b.key === 'cve'
        ? <CveBlock key={b.key} block={b} data={(summary.capabilities || {})[b.key] || scopeOf(null)} account={code} scoped={!all} get={get} post={post} />
        : <Block key={b.key} block={b} data={(summary.capabilities || {})[b.key] || scopeOf(null)} account={code} plans={plans} get={get} />))}
    </>
  );
}
